<template>
  <div :class="{ 'w-full': fullWidth }">
    <label
      :class="labelClasses"
      :for="id"
    >
      <span
        :class="{ 'sr-only': hideLabel }"
        v-text="label"
      />
      <span
        v-if="required"
        :class="{ 'sr-only': hideLabel }"
        aria-hidden="true"
        class="ml-1 text-red"
        v-text="'*'"
      />
      <!-- The space is used in lieu of left margin in order to ensure the label is announced properly by a screen reader. -->
      <span
        v-else
        class="font-normal"
        v-text="' (Optional)'"
      />
    </label>
    <select
      :id="id"
      v-model="value"
      :class="[inputClasses, { 'text-gray-500': value === '', 'border-red': errorMessage }]"
      :disabled="disabled"
      :name="name"
      :required="required"
      class="body-sm pr-10"
    >
      <option
        v-if="defaultOptionText"
        disabled
        value=""
        v-text="defaultOptionText"
      />
      <slot />
    </select>
    <div
      v-if="showErrorMessage && errorMessage"
      class="body-sm mt-1 flex items-center gap-2 text-red"
    >
      <IconExclamationCircleFilled
        :size="20"
        aria-hidden="true"
        class="text-red"
      />
      <span v-text="errorMessage" />
    </div>
    <div
      v-if="hasSlotContent($slots.helperText)"
      :id="helperTextId"
      class="body-sm mt-1"
    >
      <slot name="helperText" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useField } from 'vee-validate'
import { IconExclamationCircleFilled } from '@tabler/icons-vue'

import '@js/utils/validators'
import { useHasSlotContent } from '@js/composables/useHasSlotContent'

defineOptions({
  name: 'BaseSelectInput',
})

const props = withDefaults(
  defineProps<{
    id: string
    name: string
    label: string
    disabled?: boolean
    required?: boolean
    fullWidth?: boolean
    hideLabel?: boolean
    helperTextId?: string
    validationRules?: string
    defaultOptionText?: string
    showErrorMessage?: boolean
    labelClasses?: string | null
    inputClasses?: string | string[] | null
  }>(),
  {
    disabled: false,
    fullWidth: true,
    required: false,
    helperTextId: '',
    hideLabel: false,
    inputClasses: null,
    labelClasses: null,
    validationRules: '',
    defaultOptionText: '',
    showErrorMessage: true,
  },
)

defineModel<string | object | number>({
  default: '',
})

const { hasSlotContent } = useHasSlotContent()

const { errorMessage, value } = useField(
  () => props.name,
  props.validationRules,
  {
    syncVModel: 'model',
  },
)
</script>
